<template>
  <el-menu :default-active="activeMenu" mode="horizontal" :ellipsis="false" @select="handleSelect">
    <template v-for="(item, index) in topMenus">
      <el-menu-item v-if="index < visibleNumber" :key="index" :style="{ '--theme': theme }" :index="item.path">
        <!-- <svg-icon
          v-if="item.meta && item.meta.icon && item.meta.icon !== '#'" :icon-class="item.meta ? item.meta.icon : ''" /> -->
        <span class="mitem" :style="{ backgroundColor: (activeMenu === item.path || ['/synthesize', '/equipmentSituation', '/taskStatistics', '/dangerStatistics'].includes(activeMenu) && item.path === '/controlCenter') ? '#0052D9' : '' }">
          {{ item.meta?.title }}
        </span>
      </el-menu-item>
    </template>

    <!-- 顶部菜单超出数量折叠 -->
    <!-- <el-sub-menu v-if="topMenus.length > visibleNumber" :style="{ '--theme': theme }" index="more">
      <template #title>更多菜单</template>
      <template v-for="(item, index) in topMenus">
        <el-menu-item v-if="index >= visibleNumber" :key="index" :index="item.path"><svg-icon
            :icon-class="item.meta ? item.meta.icon : ''" /> {{ item.meta?.title }}</el-menu-item>
      </template>
    </el-sub-menu> -->
  </el-menu>
</template>

<script setup lang="ts">
import { constantRoutes } from '@/router';
import { isHttp } from '@/utils/validate';
import useAppStore from '@/store/modules/app';
import useSettingsStore from '@/store/modules/settings';
import usePermissionStore from '@/store/modules/permission';
import { getNormalPath } from '@/utils/ruoyi';
import { LocationQueryRaw, RouteRecordRaw } from 'vue-router';
import { RSAKey } from 'jsencrypt/lib/lib/jsbn/rsa';

// 顶部栏初始数
const visibleNumber = ref<number>(-1);
// 当前激活菜单的 index
const currentIndex = ref<string>();
// 隐藏侧边栏路由
const hideList = ['/index', '/user/profile'];

const appStore = useAppStore();
const settingsStore = useSettingsStore();
const permissionStore = usePermissionStore();
const route = useRoute();
const router = useRouter();

// 主题颜色
const theme = computed(() => settingsStore.theme);
// 所有的路由信息
const routers = computed(() => permissionStore.getTopbarRoutes());

// 顶部显示菜单
const topMenus = computed(() => {
  let topMenus: RouteRecordRaw[] = [];
  routers.value.map((menu) => {
    if (menu.hidden !== true) {
      // 兼容顶部栏一级菜单内部跳转
      if (menu.path === '/') {
        topMenus.push(menu.children ? menu.children[0] : menu);
      } else {
        topMenus.push(menu);
      }
    }
  });
  return topMenus;
});

// 设置子路由
const childrenMenus = computed(() => {
  let childrenMenus: RouteRecordRaw[] = [];
  routers.value.map((router) => {
    router.children?.forEach((item) => {
      if (item.parentPath === undefined) {
        if (router.path === '/') {
          item.path = '/' + item.path;
        } else {
          if (!isHttp(item.path)) {
            item.path = router.path + '/' + item.path;
          }
        }
        item.parentPath = router.path;
      }
      childrenMenus.push(item);
    });
  });
  return constantRoutes.concat(childrenMenus);
});

// 默认激活的菜单
const activeMenu = computed(() => {
  let path = route.path;
  if (path === '/index') {
    path = '/system/user';
  }
  let activePath = path;
  if (path !== undefined && path.lastIndexOf('/') > 0 && hideList.indexOf(path) === -1) {
    const tmpPath = path.substring(1, path.length);
    activePath = '/' + tmpPath.substring(0, tmpPath.indexOf('/'));
    if (!route.meta.link) {
      appStore.toggleSideBarHide(false);
    }
  } else if (!route.children) {
    activePath = path;
    appStore.toggleSideBarHide(true);
  }
  activeRoutes(activePath);
  return activePath;
});

const setVisibleNumber = () => {
  const width = document.body.getBoundingClientRect().width / 3;
  visibleNumber.value = parseInt(String(width / 85));
};

interface NavigableRouteTarget {
  path: string;
  query?: LocationQueryRaw;
}

/**
 * 拼接菜单节点的完整访问路径。
 * 绝对路径和外链保持不变，只有相对路径才继承父级目录。
 *
 * @param parentPath 父级菜单路径
 * @param routePath 当前菜单路径
 * @returns 当前菜单的完整路径
 */
const resolveMenuPath = (parentPath: string, routePath: string): string => {
  if (isHttp(routePath) || routePath.startsWith('/')) {
    return routePath;
  }
  return getNormalPath(`${parentPath}/${routePath}`);
};

/**
 * 从菜单树中查找第一个可访问的叶子路由。
 * ParentView 等目录节点没有对应页面，必须继续向下查找，避免跳转到目录路径后进入 404。
 *
 * @param routes 当前菜单节点列表
 * @param parentPath 父级菜单路径
 * @returns 第一个可访问页面及其查询参数
 */
const findFirstNavigableRoute = (routes: RouteRecordRaw[], parentPath = ''): NavigableRouteTarget | undefined => {
  for (const routeItem of routes) {
    if (routeItem.hidden) {
      continue;
    }

    const currentPath = resolveMenuPath(parentPath, routeItem.path);
    if (routeItem.children?.length) {
      const childRoute = findFirstNavigableRoute(routeItem.children, currentPath);
      if (childRoute) {
        return childRoute;
      }
      continue;
    }

    return {
      path: currentPath,
      query: routeItem.query ? JSON.parse(routeItem.query) : undefined
    };
  }
  return undefined;
};

/**
 * 切换顶部业务模块，并进入该模块首个可访问页面。
 *
 * @param key 顶部菜单路径
 */
const handleSelect = (key: string) => {
  currentIndex.value = key;
  const route = routers.value.find((item) => item.path === key);
  if (isHttp(key)) {
    // http(s):// 路径新窗口打开
    window.open(key, '_blank');
  } else if (!route || !route.children) {
    // 没有子路由路径内部打开
    const routeMenu = childrenMenus.value.find((item) => item.path === key);
    if (routeMenu && routeMenu.query) {
      let query = JSON.parse(routeMenu.query);
      router.push({ path: key, query: query });
    } else {
      router.push({ path: key });
    }
    appStore.toggleSideBarHide(true);
  } else {
    // 显示左侧联动菜单
    const sidebarRoutes = activeRoutes(key);
    const targetRoute = findFirstNavigableRoute(sidebarRoutes);
    if (targetRoute) {
      if (isHttp(targetRoute.path)) {
        window.open(targetRoute.path, '_blank');
      } else {
        router.push(targetRoute);
      }
    }
    appStore.toggleSideBarHide(false);
  }
};

const activeRoutes = (key: string) => {
  let routes: RouteRecordRaw[] = [];
  if (childrenMenus.value && childrenMenus.value.length > 0) {
    childrenMenus.value.map((item) => {
      if (key == item.parentPath || (key == 'index' && '' == item.path)) {
        routes.push(item);
      }
    });
  }
  if (routes.length > 0) {
    permissionStore.setSidebarRouters(routes);
  } else {
    appStore.toggleSideBarHide(true);
  }
  return routes;
};

onMounted(() => {
  window.addEventListener('resize', setVisibleNumber);
});
onBeforeUnmount(() => {
  window.removeEventListener('resize', setVisibleNumber);
});

onMounted(() => {
  setVisibleNumber();
});
</script>

<style lang="scss">
.topmenu-container.el-menu--horizontal>.el-menu-item {
  float: left;
  height: 50px !important;
  line-height: 50px !important;
  color: #999093 !important;
  padding: 0 5px !important;
  margin: 0 10px !important;
  box-sizing: border-box;
}

.topmenu-container.el-menu--horizontal>.el-menu-item.is-active,
.el-menu--horizontal>.el-sub-menu.is-active .el-submenu__title {
  border-bottom: 2px solid #{'var(--theme)'} !important;
  color: #303133;
}

/* sub-menu item */
.topmenu-container.el-menu--horizontal>.el-sub-menu .el-sub-menu__title {
  float: left;
  height: 50px !important;
  line-height: 50px !important;
  color: #999093 !important;
  padding: 0 5px !important;
  margin: 0 10px !important;
}

/* 背景色隐藏 */
.topmenu-container.el-menu--horizontal>.el-menu-item:not(.is-disabled):focus,
.topmenu-container.el-menu--horizontal>.el-menu-item:not(.is-disabled):hover,
.topmenu-container.el-menu--horizontal>.el-submenu .el-submenu__title:hover {
  background-color: rgba(3, 11, 31, 1);

}

/* 图标右间距 */
.topmenu-container .svg-icon {
  margin-right: 4px;
}

.topmenu-container.el-menu--horizontal>.el-menu-item {
  margin: 0px !important;
}

.el-menu--horizontal {
  height: 50px !important;
}

.topmenu-container.el-menu--horizontal>.el-menu-item.is-active,
.el-menu--horizontal>.el-sub-menu.is-active .el-submenu__title {
  border-bottom: none !important;
}

.el-menu--horizontal>.el-menu-item {
  border-bottom: none !important;
}

.el-menu--horizontal.el-menu {
  border-bottom: none !important;
}

.mitem {
  color: #fff;
  height: 30px;
  line-height: 30px;
  padding: 0 10px;
  border-radius: 4px;
}
</style>
